import React, { useState, useEffect } from 'react';
import { MessageSquare, RefreshCw, Eye, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_STATUSES = {
  new: { label: 'Nouveau', color: 'default' },
  assigned: { label: 'Assigné', color: 'blue' },
  waiting_user: { label: 'Attente de votre réponse', color: 'purple' },
  waiting_team: { label: 'En traitement', color: 'indigo' },
  in_progress: { label: 'En cours', color: 'yellow' },
  resolved: { label: 'Résolu', color: 'green' },
  closed: { label: 'Fermé', color: 'secondary' }
};

const SUPPORT_PRIORITIES = {
  low: { label: '🔵 Basse', color: 'blue' },
  medium: { label: '🟡 Moyenne', color: 'yellow' },
  high: { label: '🟠 Haute', color: 'orange' },
  critical: { label: '🔴 Critique', color: 'destructive' }
};

const MyTickets = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  // Fetch my tickets
  const fetchMyTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/beta/support/tickets/my`, {
        withCredentials: true
      });

      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Erreur lors du chargement de vos tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchMyTickets();
    }
  }, [user]);

  // Open detail modal
  const handleViewDetails = async (ticket) => {
    try {
      const response = await axios.get(`${API}/admin/beta/support/tickets/${ticket.ticket_id}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
      setNewMessage('');
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Add message
  const handleAddMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}/message`,
        { message: newMessage, attachments: [] },
        { withCredentials: true }
      );

      toast.success('Message ajouté avec succès');
      setNewMessage('');
      
      // Refresh ticket details
      const response = await axios.get(`${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
      fetchMyTickets(); // Refresh list
    } catch (error) {
      console.error('Failed to add message:', error);
      toast.error('Erreur lors de l\'ajout du message');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Mes Tickets de Support</h1>
          <p className="text-muted-foreground mt-2">
            Suivez l'état de vos demandes de support
          </p>
        </div>
        <Button variant="outline" onClick={fetchMyTickets}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              En cours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {tickets.filter(t => !['resolved', 'closed'].includes(t.status)).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-500" />
              Attente de réponse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-purple-600">
              {tickets.filter(t => t.status === 'waiting_user').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Résolus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Vos tickets ({tickets.length})</CardTitle>
          <CardDescription>
            Cliquez sur un ticket pour voir les détails et la conversation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Chargement...</p>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Vous n'avez aucun ticket de support
              </p>
              <Button asChild>
                <a href="/support/contact">
                  Créer un ticket
                </a>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.ticket_id}>
                      <TableCell>
                        <Badge variant={SUPPORT_PRIORITIES[ticket.priority]?.color || 'default'}>
                          {SUPPORT_PRIORITIES[ticket.priority]?.label || ticket.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="font-medium">{ticket.subject}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {ticket.description.substring(0, 60)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{ticket.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={SUPPORT_STATUSES[ticket.status]?.color || 'default'}>
                          {SUPPORT_STATUSES[ticket.status]?.label || ticket.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(ticket.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(ticket)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedTicket && (
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTicket.subject}</DialogTitle>
              <DialogDescription>
                Ticket #{selectedTicket.ticket_id.substring(0, 8)} • {selectedTicket.category}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Status & Priority */}
              <div className="flex gap-3">
                <Badge variant={SUPPORT_STATUSES[selectedTicket.status]?.color || 'default'} className="text-sm">
                  {SUPPORT_STATUSES[selectedTicket.status]?.label || selectedTicket.status}
                </Badge>
                <Badge variant={SUPPORT_PRIORITIES[selectedTicket.priority]?.color || 'default'} className="text-sm">
                  {SUPPORT_PRIORITIES[selectedTicket.priority]?.label || selectedTicket.priority}
                </Badge>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="font-medium">Description</Label>
                <p className="text-sm p-3 bg-muted rounded-md whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>

              {/* Conversation */}
              <div className="space-y-2">
                <Label className="font-medium">Conversation</Label>
                <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-muted/30 rounded-lg">
                  {selectedTicket.messages.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      Aucun message encore. En attente de réponse de l'équipe support.
                    </p>
                  ) : (
                    selectedTicket.messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`p-3 rounded-lg ${
                          msg.from_type === 'admin'
                            ? 'bg-blue-50 dark:bg-blue-950/20 ml-8'
                            : 'bg-white dark:bg-slate-800 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={msg.from_type === 'admin' ? 'default' : 'secondary'}>
                            {msg.from_type === 'admin' ? '👤 Support' : '👥 Vous'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add message (only if not closed) */}
              {!['closed', 'resolved'].includes(selectedTicket.status) && (
                <div className="space-y-2">
                  <Label htmlFor="new-message-user">Ajouter une réponse</Label>
                  <Textarea
                    id="new-message-user"
                    placeholder="Votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleAddMessage}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Envoyer
                    </Button>
                  </div>
                </div>
              )}

              {/* Info if closed */}
              {['closed', 'resolved'].includes(selectedTicket.status) && (
                <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    ✅ Ce ticket a été {selectedTicket.status === 'closed' ? 'fermé' : 'résolu'}. 
                    Si vous avez encore besoin d'aide, veuillez créer un nouveau ticket.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MyTickets;
